import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Space, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";

/** Serial numbers from pasted text or a CSV/TXT file: one per line, or the first column. */
export function parseSerials(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const first = raw.split(/[,;\t]/)[0]?.trim().replace(/^"|"$/g, "") ?? "";
    // A header cell ("IMEI", "Serial No", "SN") is skipped; "SN-001" is not one.
    if (first && !/^(imei\d?|serial( ?(no|number))?|sn|no|nomor( seri)?)$/i.test(first) && !out.includes(first)) out.push(first);
  }
  return out;
}

/**
 * Import IMEI / serial numbers (Plan §6.21): paste the supplier's list, scan
 * into the box, or pick a CSV/TXT file (first column). The server names any
 * number it refuses.
 */
export function SerialImportModal({ title, help, max, onSubmit, onClose }: {
  title: string;
  help?: string;
  /** Units still without a serial, when known. */
  max?: number;
  onSubmit: (serials: string[]) => Promise<unknown>;
  onClose: () => void;
}) {
  const { t, formatNumber } = useI18n();
  const errorText = useErrorText();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const serials = parseSerials(text);
  const tooMany = max !== undefined && serials.length > max;
  const pickFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const add = (s: string) => setText((cur) => (cur ? cur + "\n" : "") + s);
      setError(null);
      if (!/\.xlsx$/i.test(file.name)) return add(await file.text());
      // Excel is read by the server (column A of the first sheet), numbers whole.
      setReading(true);
      try {
        const r = await unwrap(
          api.POST("/v1/serials/read-file", {
            body: file as unknown as string,
            bodySerializer: (b: unknown) => b as Blob,
            headers: { "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
          }),
        );
        add(r.values.join("\n"));
      } catch (err) {
        setError(errorText(err));
      } finally {
        setReading(false);
      }
    };
    input.click();
  };
  return (
    <Modal
      open
      title={title}
      okText={t("serial.import.ok", { n: formatNumber(serials.length) })}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: serials.length === 0 || tooMany }}
      confirmLoading={busy}
      onCancel={onClose}
      onOk={async () => {
        setBusy(true);
        setError(null);
        try {
          await onSubmit(serials);
          onClose();
        } catch (err) {
          setError(errorText(err));
        } finally {
          setBusy(false);
        }
      }}
      destroyOnHidden
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{help ?? t("serial.import.help")}</Typography.Text>
        <Input.TextArea autoFocus rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={"356938035643809\n356938035643817\n..."} className="z-num" />
        <Space wrap>
          <Button icon={<UploadOutlined />} loading={reading} onClick={pickFile}>
            {t("serial.import.file")}
          </Button>
          <Typography.Text type={tooMany ? "danger" : "secondary"}>
            {max !== undefined ? t("serial.import.countOf", { n: formatNumber(serials.length), max: formatNumber(max) }) : t("serial.import.count", { n: formatNumber(serials.length) })}
          </Typography.Text>
        </Space>
        {error && <Alert type="error" showIcon title={error} />}
      </Space>
    </Modal>
  );
}
